import React, { useState, useCallback, useEffect } from "react";
import { withAppHOC } from "../../../hoc";
import PageHeading from "../../../components/PageHeader/PageHeader";
import OverlayLoader from "../../../components/OverlayLoader/OverlayLoader";
import { Grid, GridItem } from "../../../components/Grid";
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  TextInput,
  NumberInput,
  Dropdown,
  MessageBox,
  CreateButton,
  ResetButton,
  PasswordInput,
  Switch,
} from "../../../components/FormElements";
import API from "../../../api";
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from "../../../constants";
import { getReturnUrl } from "../../../utils";
import { StyledSwitchContainer } from "../../../components/Styled";

const UsersCreate = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [fullName, setFullname] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileNumberVerificationStatus, setMobileNumberVerificationStatus] =
    useState("Pending");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [topupStatus, setTopupStatus] = useState("Active");
  const [withdrawStatus, setWithdrawStatus] = useState("Active");
  const [canSwitchFunds, setCanSwitchFunds] = useState("Active");
  const [userStatus, setUserStatus] = useState("Active");
  const [canEditApis, setCanEditApis] = useState("Inactive");
  const [complaintRatio, setComplaintRatio] = useState("100");

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus("");
    setResponseMessage("");
  };

  const resetForm = () => {
    setFullname("");
    setMobileNumber("");
    setMobileNumberVerificationStatus("Pending");
    setPassword("");
    setConfirmPassword("");
    setTopupStatus("Active");
    setWithdrawStatus("Active");
    setCanSwitchFunds("Active");
    setUserStatus("Active");
    setCanEditApis("Inactive");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    const formData = {
      fullName,
      mobileNumber,
      mobileNumberVerificationStatus,
      password,
      confirmPassword,
      topupStatus,
      withdrawStatus,
      canSwitchFunds,
      userStatus,
      complaintRatio,
      canEditApis,
    };
    API.post("/accounts-management/users", formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          resetForm();
          setTimeout(() => {
            resetResponseData();
            window.location.href = getReturnUrl(PERMISSION_TYPES.CREATE);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const readAppConfig = useCallback(() => {
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.get(`/configurations/app-configuration`)
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          const { complaintRatioAllowed } = data;
          setComplaintRatio(complaintRatioAllowed);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    readAppConfig();
  }, [readAppConfig]);

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method="POST" action="#" onSubmit={onSubmit}>
          <Grid columns={"calc(50% - 10px) calc(50% - 10px)"}>
            <GridItem>
              <Fieldset>
                <Label required>Full Name</Label>
                <TextInput
                  value={fullName}
                  onChange={setFullname}
                  placeholder="Please enter full name"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Mobile Number</Label>
                <NumberInput
                  value={mobileNumber}
                  onChange={setMobileNumber}
                  placeholder="Please enter mobile number"
                  maxLength={10}
                  disabled={isLoading}
                  pattern={`[0-9]*`}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid columns={"calc(50% - 10px) calc(50% - 10px)"}>
            <GridItem>
              <Fieldset>
                <Label required>Password</Label>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  placeholder="Please enter password"
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Confirm Password</Label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="Please re-enter password"
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid>
            <GridItem>
              <Fieldset>
                <Label>Wallet Topup</Label>
                <StyledSwitchContainer>
                  <Switch
                    value={topupStatus === "Active"}
                    onChange={(value) => {
                      setTopupStatus(value ? "Active" : "Inactive");
                    }}
                  />
                </StyledSwitchContainer>
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label>Redeem Funds</Label>
                <StyledSwitchContainer>
                  <Switch
                    value={withdrawStatus === "Active"}
                    onChange={(value) => {
                      setWithdrawStatus(value ? "Active" : "Inactive");
                    }}
                  />
                </StyledSwitchContainer>
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label>Switch Funds</Label>
                <StyledSwitchContainer>
                  <Switch
                    value={canSwitchFunds === "Active"}
                    onChange={(value) => {
                      setCanSwitchFunds(value ? "Active" : "Inactive");
                    }}
                  />
                </StyledSwitchContainer>
              </Fieldset>
            </GridItem>
          </Grid>
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <CreateButton disabled={isLoading} type="submit">
              Create
            </CreateButton>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(UsersCreate);
